import { Button } from "components/DesignSystem/Button/Button";
import { HighlightSearchTerm } from "components/DesignSystem/HighlightText";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { Cart } from "components/icons/Cart";
import { ProductCard } from "components/ProductCard/ProductCard";
import { AddonPricing, GetDescription } from "components/SubscriptionSummary";
import {
  BOOKKEEPING_SOFTWARE,
  BOOKKEEPING_V2,
  FRACTIONAL_PAYROLL_ADMINISTRATOR,
  REGISTERED_AGENT,
  SALES_TAX_FILING,
  _MANAGE,
} from "constants/addons";
import dayjs from "dayjs";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useQuery } from "hooks/useQuery";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useDispatch } from "react-redux";
import { chatApi, useCreateMessageDraftMutation } from "store/apis/chat";
import { AddonData, setAddonData, setAddonType } from "store/slices/addons";
import { toggleCartSubscription } from "store/slices/cartSubscription";
import { openFloatingChat, setToOpenChatId } from "store/slices/chat";
import { Subscription } from "types/Models/subscription";
import { formatDate } from "utils/formatDate";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useGetCartQuery } from "store/apis/products";
import ToolTip from "components/design/toolTip";
import { CartAdded } from "components/icons/CartAdded";

export const SubscriptionTypeCard = ({
  subscription,
  isPrimarySubscription,
  isDiscountApplied,
}: {
  subscription: Subscription;
  isPrimarySubscription?: boolean;
  isDiscountApplied?: boolean;
}) => {
  const dispatch = useDispatch();
  const query = useQuery();
  const search = query.get("search") || "";
  const { isAdmin } = useRoleBasedView();
  const { secondary_channel_url } = useCurrentGroupContext();
  const [createUpdateDraft, { isLoading }] = useCreateMessageDraftMutation();

  const { uuid: groupId, entities } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();

  const { data: cart } = useGetCartQuery(
    { groupId: groupId!, entityId },
    { skip: !groupId || !entityId }
  );

  const inCartProducts = cart?.cart_items.filter(
    (item) => item.product_details.product_id === subscription.uuid
  );

  const combinedStateSeasonInCart = inCartProducts
    ?.map((item) => {
      const stateName = item.ra_state_name;
      const season = item.product_details.season;
      return stateName && season
        ? `${stateName}-${season}`
        : stateName || season;
    })
    .filter(Boolean)
    .join(", ");

  const currentEntityName = entities.find(
    (item) => item.uuid === entityId
  )?.name;

  const bookkeepingSubscriptionName =
    subscription.subscription_type === BOOKKEEPING_V2 &&
    subscription.subscription_name;

  const onSubscribeHandler = async (
    addon: AddonData,
    useCart: boolean = false
  ) => {
    if (isBooksService && !subscription.group_subscription && !isAdmin) {
      if (secondary_channel_url) {
        const messageData = await createUpdateDraft({
          channelUrl: secondary_channel_url,
          message: `I would like to subscribe to the Monthly Bookkeeping Service, please let me know the next steps.`,
        });

        if (messageData.data) {
          await dispatch(
            chatApi.util.upsertQueryData(
              "getDraftMessage",
              { channelUrl: secondary_channel_url },
              messageData.data
            )
          );
        }
      }

      dispatch(setToOpenChatId(secondary_channel_url));
      return dispatch(openFloatingChat());
    }
    dispatch(toggleCartSubscription(useCart));
    dispatch(setAddonData(addon));
    dispatch(setAddonType(addon?.subscription_type!));
  };

  const onManageHandler = (addon: AddonData) => {
    dispatch(setAddonData(addon));
    dispatch(setAddonType(addon?.subscription_type + _MANAGE));
  };

  const isSubscribed =
    subscription.subscription_type !== "REGISTERED_AGENT" &&
    subscription.subscription_type !== SALES_TAX_FILING &&
    subscription.group_subscription;

  const minAmount = subscription.ra_state_amount_maps?.length
    ? subscription.ra_state_amount_maps.reduce(
        (min, { amount }) => Math.min(min, Number(amount)),
        Infinity
      )
    : subscription.amount;

  const isBooksService = subscription.subscription_type === BOOKKEEPING_V2;

  return (
    <ProductCard
      key={subscription.uuid}
      isPrimarySubscription={isPrimarySubscription}
      title={
        <HighlightSearchTerm
          text={
            isBooksService && !subscription.group_subscription
              ? "Bookkeeping Service - Subscription"
              : subscription.subscription_name
          }
          searchTerm={search}
        />
      }
      description={
        subscription.subscription_type === "REGISTERED_AGENT"
          ? "Registered Agent service for all states of the United States of America"
          : !subscription.stripe_coupon && (
              <GetDescription
                type={subscription.subscription_type}
                subscriptions={subscription.group_subscription}
              />
            )
      }
      price={
        (isBooksService && !subscription.group_subscription) ||
        (subscription.subscription_type === FRACTIONAL_PAYROLL_ADMINISTRATOR &&
          isSubscribed) ? null : (
          <AddonPricing
            addon={
              subscription.subscription_type !== "REGISTERED_AGENT"
                ? subscription
                : { ...subscription, amount: minAmount }
            }
          />
        )
      }
      action={
        isSubscribed ? (
          <div className="t-flex t-gap-1">
            {subscription.scheduled_date &&
              dayjs(subscription.scheduled_date).isAfter(dayjs()) && (
                <Tag tagType="gray" icon={false}>
                  Starts on {formatDate(subscription?.scheduled_date)}
                </Tag>
              )}
            <Button size="small" onClick={() => onManageHandler(subscription)}>
              Manage plan
            </Button>
            <div>
              {[REGISTERED_AGENT, BOOKKEEPING_SOFTWARE].includes(
                subscription.subscription_type
              ) && (
                <Button
                  customType="icon"
                  size="small"
                  onClick={() => onSubscribeHandler(subscription, true)}
                >
                  <Cart />
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="t-flex t-items-center t-justify-between">
            <div className="t-flex t-gap-1">
              <Button
                size="small"
                isLoading={isLoading}
                onClick={() => onSubscribeHandler(subscription, false)}
              >
                {isBooksService && !isAdmin ? "View details" : "Subscribe"}
              </Button>
              {((isBooksService &&
                !subscription.group_subscription &&
                isAdmin) ||
                !isBooksService) && (
                <Button
                  customType="icon"
                  size="small"
                  onClick={() => onSubscribeHandler(subscription, true)}
                >
                  <Cart />
                </Button>
              )}
            </div>
            {isAdmin && inCartProducts && inCartProducts?.length ? (
              <ToolTip
                text={`Added for: ${
                  bookkeepingSubscriptionName ||
                  combinedStateSeasonInCart ||
                  currentEntityName
                }`}
              >
                <div className="t-text-caption t-text-text-30 t-flex t-items-center t-gap-1 -t-mb-6 t-mr-1">
                  <CartAdded />
                  <div>Added to cart</div>
                </div>
              </ToolTip>
            ) : null}
          </div>
        )
      }
      planStatus={Boolean(isSubscribed) ? "ACTIVE" : undefined}
      isSubscription
      isDiscountApplied={isDiscountApplied}
    />
  );
};
